import palette from "assets/theme/base/palette";
import React, { useState, useMemo } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import FaqData from "./faqData";
import BestPracticesData from "./bestPracticesData";
import { useTranslation } from "react-i18next";

const TAB_ICONS = ["directions_run", "supervisor_account", "sports"];

// const BP_ICONS = [
//   "schedule", "bar_chart", "lightbulb", "people", "menu_book",
//   "track_changes", "flag", "star",
// ];

const CATEGORIES = [
  { id: "all",      label: "General",               icon: "help_outline" },
  { id: "account",  label: "Account & Login",        icon: "lock_outline" },
  { id: "assess",   label: "Assessments",             icon: "assignment" },
  { id: "content",  label: "Content & Reports",       icon: "description" },
  { id: "billing",  label: "Subscriptions & Billing", icon: "credit_card" },
  { id: "tech",     label: "Technical Issues",        icon: "build" },
  { id: "privacy",  label: "Privacy & Security",      icon: "security" },
];

const FAQ_CATEGORIES = [
  "all", "all", "all",
  "account", "account", "account",
  "assess", "assess", "assess", "assess", "assess",
  "content", "content", "content",
  "privacy", "content", "privacy", "privacy",
  "billing", "billing",
  "tech",
];

const Faq = () => {
  const { t } = useTranslation();
  const faqData          = FaqData();
  const bestPracticesData = BestPracticesData();

  const [activeTab, setActiveTab]         = useState(0);
  const [activeCategory, setActiveCategory] = useState("all");
  const [expanded, setExpanded]           = useState("");
  const [expandAll, setExpandAll]         = useState(false);
  const [searchQuery, setSearchQuery]     = useState("");

  const handleAccordion = (panel) => (_, isExpanded) =>
    setExpanded(isExpanded ? panel : "");

  const toggleExpandAll = () => {
    setExpandAll((prev) => !prev);
    setExpanded("");
  };

  const filteredFaqs = useMemo(() => {
    return faqData
      .map((item, i) => ({ ...item, index: i, category: FAQ_CATEGORIES[i] || "all" }))
      .filter((item) => {
        const matchCat = activeCategory === "all" || item.category === activeCategory;
        const matchSearch = !searchQuery || item.question.toLowerCase().includes(searchQuery.toLowerCase());
        return matchCat && matchSearch;
      });
  }, [faqData, activeCategory, searchQuery]);

  const currentBP = bestPracticesData[activeTab];

  const mailtoHref = `mailto:support@maxu.co?subject=${encodeURIComponent(
    t("support.subject", { email: "" })
  )}&body=${encodeURIComponent(t("support.body"))}`;

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.faqs")} />

      <MDBox pt={2} pb={4}>

        {/* ── Best Practices ── */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          {/* Persona tabs */}
          <MDBox sx={{ borderBottom: `2px solid ${palette.neutral[200]}`, display: "flex" }}>
            {bestPracticesData.map((bp, i) => (
              <MDBox
                key={i}
                onClick={() => setActiveTab(i)}
                display="flex" alignItems="center" gap={1}
                sx={{
                  flex: 1, px: 3, py: 1.75, cursor: "pointer",
                  justifyContent: "center",
                  borderBottom: activeTab === i ? `3px solid ${palette.brand[500]}` : "3px solid transparent",
                  bgcolor: activeTab === i ? palette.brand[50] : "#fff",
                  transition: "all 200ms",
                  "&:hover": { bgcolor: palette.brand[50] },
                }}
              >
                <Icon sx={{ color: activeTab === i ? palette.brand[500] : "text.secondary", fontSize: "20px !important" }}>
                  {TAB_ICONS[i]}
                </Icon>
                <MDTypography
                  variant="body2"
                  fontWeight={activeTab === i ? "bold" : "regular"}
                  sx={{ color: activeTab === i ? palette.brand[500] : "text.secondary" }}
                >
                  {bp.persona}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>

          {/* Best practices content */}
          <MDBox p={3}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{ color: palette.brand[600], letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              BEST PRACTICES FOR {currentBP?.persona?.toUpperCase()}
            </MDTypography>

            <Grid container spacing={3} mt={0.5}>
              {/* Left decorative panel */}
              <Grid item xs={12} md={3}>
                <MDBox
                  sx={{
                    height: "100%", minHeight: 200, borderRadius: 3,
                    bgcolor: palette.brand[50],
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    p: 2.5, textAlign: "center",
                  }}
                >
                  <MDBox
                    sx={{
                      width: 80, height: 80, borderRadius: "50%",
                      bgcolor: palette.brand[100],
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: "40px !important", color: palette.brand[500] }}>
                      {TAB_ICONS[activeTab]}
                    </Icon>
                  </MDBox>
                  {currentBP?.description && (
                    <MDTypography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {currentBP.description}
                    </MDTypography>
                  )}
                  {!currentBP?.description && (
                    <MDTypography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Follow these best practices to get the most value from your MaxU experience.
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>

              {/* Right tips list */}
              <Grid item xs={12} md={9}>
                <MDBox sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: 2, overflow: "hidden" }}>
                  {currentBP?.bestPractice.map((tip, i) => {
                    const isObj = typeof tip === "object" && tip !== null;
                    const mainText = isObj ? tip.main : tip;
                    const details  = isObj ? tip.details : [];
                    return (
                      <MDBox key={i}>
                        <MDBox display="flex" alignItems="flex-start" gap={2} px={2.5} py={1.75}>
                          {/* Number badge */}
                          <MDBox
                            sx={{
                              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                              bgcolor: palette.brand[500],
                              display: "flex", alignItems: "center", justifyContent: "center",
                              mt: 0.1,
                            }}
                          >
                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#fff", fontSize: "0.7rem" }}>
                              {i + 1}
                            </MDTypography>
                          </MDBox>

                          {/* Icon */}
                          {/* <MDBox
                            sx={{
                              width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
                              bgcolor: `${palette.brand[500]}14`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                          >
                            <Icon sx={{ color: palette.brand[500], fontSize: "18px !important" }}>
                              {BP_ICONS[i] || "check_circle"}
                            </Icon>
                          </MDBox> */}

                          {/* Text */}
                          <MDBox flex={1}>
                            <MDTypography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {mainText}
                            </MDTypography>
                            {details.length > 0 && (
                              <MDBox component="ul" sx={{ pl: 2, mt: 0.75, mb: 0 }}>
                                {details.map((d, di) => (
                                  <MDTypography key={di} component="li" variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, mb: 0.25 }}>
                                    {d}
                                  </MDTypography>
                                ))}
                              </MDBox>
                            )}
                          </MDBox>
                        </MDBox>
                        {i < currentBP.bestPractice.length - 1 && <Divider sx={{ m: 0 }} />}
                      </MDBox>
                    );
                  })}
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>

          {/* ── Search Bar ── */}
        <MDBox mb={3}>
          <MDInput
            fullWidth
            placeholder="Search FAQs, topics, questions..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setActiveCategory("all"); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: "text.secondary", fontSize: "20px !important" }}>search</Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px", bgcolor: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              },
            }}
          />
        </MDBox>

        {/* ── FAQ Section ── */}
        <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", mb: 3, p: 3 }}>
          {/* Section header */}
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
            <MDTypography
              variant="caption" fontWeight="bold"
              sx={{ color: palette.brand[600], letterSpacing: "0.08em", textTransform: "uppercase", fontSize: "0.85rem" }}
            >
              Frequently Asked Questions
            </MDTypography>
            <MDBox
              display="flex" alignItems="center" gap={0.5}
              onClick={toggleExpandAll}
              sx={{ cursor: "pointer" }}
            >
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: palette.brand[500] }}>
                {expandAll ? "Collapse All" : "Expand All"}
              </MDTypography>
              <Icon sx={{ fontSize: "18px !important", color: palette.brand[500] }}>
                {expandAll ? "unfold_less" : "unfold_more"}
              </Icon>
            </MDBox>
          </MDBox>

          <Grid container spacing={2}>
            {/* Left category sidebar */}
            <Grid item xs={12} md={3}>
              <MDBox sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: 2, overflow: "hidden" }}>
                {CATEGORIES.map((cat, i) => (
                  <MDBox key={cat.id}>
                    <MDBox
                      display="flex" alignItems="center" gap={1.5} px={2} py={1.5}
                      onClick={() => { setActiveCategory(cat.id); setSearchQuery(""); }}
                      sx={{
                        cursor: "pointer",
                        bgcolor: activeCategory === cat.id ? palette.brand[50] : "#fff",
                        borderLeft: activeCategory === cat.id ? `3px solid ${palette.brand[500]}` : "3px solid transparent",
                        transition: "all 150ms",
                        "&:hover": { bgcolor: palette.brand[50] },
                      }}
                    >
                      <Icon sx={{ fontSize: "18px !important", color: activeCategory === cat.id ? palette.brand[500] : "text.secondary" }}>
                        {cat.icon}
                      </Icon>
                      <MDTypography
                        variant="body2"
                        fontWeight={activeCategory === cat.id ? "bold" : "regular"}
                        sx={{ color: activeCategory === cat.id ? palette.brand[600] : "text.primary" }}
                      >
                        {cat.label}
                      </MDTypography>
                    </MDBox>
                    {i < CATEGORIES.length - 1 && <Divider sx={{ m: 0 }} />}
                  </MDBox>
                ))}
              </MDBox>
            </Grid>

            {/* Right FAQ accordion list */}
            <Grid item xs={12} md={9}>
              {filteredFaqs.length === 0 ? (
                <MDBox textAlign="center" py={5}>
                  <Icon sx={{ fontSize: "40px !important", color: palette.neutral[300], mb: 1 }}>search_off</Icon>
                  <MDTypography variant="body2" color="text.secondary">
                    No questions found matching your search.
                  </MDTypography>
                </MDBox>
              ) : (
                filteredFaqs.map((item) => {
                  const panelId  = `faq-${item.index}`;
                  const isOpen   = expandAll || expanded === panelId;
                  return (
                    <Accordion
                      key={panelId}
                      expanded={isOpen}
                      onChange={!expandAll ? handleAccordion(panelId) : undefined}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 1,
                        border: `1px solid ${palette.neutral[200]}`,
                        borderRadius: "10px !important",
                        "&:before": { display: "none" },
                        overflow: "hidden",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Icon sx={{ fontSize: "20px !important", color: "text.secondary" }}>
                            {isOpen ? "keyboard_arrow_down" : "chevron_right"}
                          </Icon>
                        }
                        sx={{
                          px: 2, py: 0.5, minHeight: 52,
                          bgcolor: isOpen ? palette.neutral[50] : "#fff",
                        }}
                      >
                        <MDTypography variant="body2" fontWeight="medium">{item.question}</MDTypography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2, bgcolor: "#fff" }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <MDBox component="ul" sx={{ pl: 0, m: 0, listStyle: "none" }}>
                          {item.answer.map((ans, ai) => (
                            <MDBox key={ai} display="flex" alignItems="flex-start" gap={1} mb={0.75}>
                              <Icon sx={{ color: palette.brand[400], fontSize: "16px !important", mt: 0.2, flexShrink: 0 }}>
                                check_circle
                              </Icon>
                              <MDTypography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                {ans}
                              </MDTypography>
                            </MDBox>
                          ))}
                        </MDBox>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Grid>
          </Grid>
        </Card>

        {/* ── Still Need Help Banner ── */}
        <Card
          sx={{
            borderRadius: 3, overflow: "hidden",
            border: `1px solid ${palette.neutral[200]}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} p={3}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  width: 56, height: 56, borderRadius: "50%",
                  bgcolor: palette.brand[50],
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: "28px !important", color: palette.brand[500] }}>headset_mic</Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold">Still need help?</MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Our support team is here for you.
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Email us at{" "}
                  <MDBox component="a" href="mailto:support@maxu.co" sx={{ color: palette.brand[500], fontWeight: 600, textDecoration: "none" }}>
                    support@maxu.co
                  </MDBox>
                  {" "}and we&apos;ll get back to you as soon as possible.
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDButton
              component="a"
              href={mailtoHref}
              variant="contained"
              color="primary"
              startIcon={<Icon>email</Icon>}
              sx={{ borderRadius: "12px", px: 3, py: 1.25, fontWeight: 700, textTransform: "none" }}
            >
              Contact Support
            </MDButton>
          </MDBox>
        </Card>

      </MDBox>
    </DashboardLayout>
  );
};

export default Faq;
